// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/mission-launch/MissionScan.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';
import MissionCard from './shared/MissionCard';
import AnimatedProgressStrip from './shared/AnimatedProgressStrip';

const defaultScanSteps = [
  'Verifying connector authorization',
  'Preparing the governed attempt',
  'Dispatching AWS S3 observation',
  'Awaiting persisted AWS evidence',
  'Validating evidence provenance',
  'Assessing S3 configuration',
  'Evaluating bounded control observations',
  'Applying governed completion policy',
  'Preparing the Mission Brief',
];

const PULSE_INTERVAL_MS = 650;

function clampPercent(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(100, Math.round(numeric)));
}

function buildScanSteps(progress, completed, failed) {
  const normalizedProgress = clampPercent(progress);
  const activeIndex = Math.min(
    defaultScanSteps.length - 1,
    Math.floor((normalizedProgress / 100) * defaultScanSteps.length)
  );

  return defaultScanSteps.map((label, index) => {
    if (completed) return { label, status: 'complete' };
    if (failed && index === activeIndex) return { label, status: 'failed' };
    if (index < activeIndex) return { label, status: 'complete' };
    if (index === activeIndex) return { label, status: 'active' };
    return { label, status: 'pending' };
  });
}

export default function MissionScan({
  autoStart = false,
  connectedConnectorCount = 0,
  selectedConnectorCount = 0,
  scanStatus = 'not_started',
  scanRuntime = {},
  scanError = '',
  scanAttempts = [],
  unifiedAwsProgress = null,
  retrying = false,
  onStart,
  onRefresh,
  onRetry,
  onRescan,
  onComplete,
}) {
  const [pulseHigh, setPulseHigh] = useState(false);
  const [starting, setStarting] = useState(false);
  const autoStartAttemptedRef = useRef(false);
  const mountedRef = useRef(true);

  const normalizedStatus = String(
    scanStatus || scanRuntime?.status || 'not_started'
  ).trim().toLowerCase();

  const scanRunning =
    normalizedStatus === 'running' || normalizedStatus === 'in_progress';
  const scanComplete = normalizedStatus === 'complete';
  const scanFailed = normalizedStatus === 'failed';
  const progress = scanComplete
    ? 100
    : clampPercent(scanRuntime?.progress || 0);
  const latestAttempt =
    Array.isArray(scanAttempts) && scanAttempts.length
      ? scanAttempts[scanAttempts.length - 1]
      : null;

  const coreStateClass = scanComplete
    ? 'mission-scan-core-complete'
    : scanRunning
      ? 'mission-scan-core-running'
      : 'mission-scan-core-idle';

  const pulseStateClass = scanRunning
    ? pulseHigh
      ? 'mission-scan-core-pulse-high'
      : 'mission-scan-core-pulse-low'
    : '';

  const steps = useMemo(
    () => buildScanSteps(progress, scanComplete, scanFailed),
    [progress, scanComplete, scanFailed]
  );

  const hasConnectedEvidence = Number(connectedConnectorCount || 0) > 0;

  // Only display four-service coverage from an explicit, attempt-scoped,
  // authoritative customer projection. Never infer IAM/CloudTrail/Config
  // success from the legacy S3 scan completion percentage.
  // The GET /mission-launch/scan/status response is the sole read input.
  // Never use scan percentage or a different attempt to infer AWS evidence.
  const projected = unifiedAwsProgress?.schema === 'oasys.mission_scan.unified_progress_optional.v1'
    && unifiedAwsProgress?.state === 'available'
    && unifiedAwsProgress?.source === 'authoritative_active_attempt'
    && unifiedAwsProgress?.scan_complete === false
    && unifiedAwsProgress?.assurance_evaluated === false
    ? unifiedAwsProgress.progress : null;
  const authoritativeServices = projected?.schema === 'oasys.mission_scan.customer_progress.v1'
    && projected?.source === 'authoritative_unified_active_runtime'
    && projected?.assurance_evaluated === false
    && projected?.scan_complete === false
    && !scanComplete
    && projected?.attempt_id === (latestAttempt?.attempt_id || scanRuntime?.attempt_id)
    && projected?.services && ['s3', 'iam', 'cloudtrail', 'aws_config'].every(
      (key) => projected.services[key] && typeof projected.services[key].state === 'string'
    ) ? projected.services : null;
  const serviceLabels = { s3: 'Amazon S3', iam: 'AWS IAM', cloudtrail: 'AWS CloudTrail', aws_config: 'AWS Config' };


  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  useEffect(() => {
    if (!scanRunning) {
      setPulseHigh(false);
      return undefined;
    }

    const timer = window.setInterval(() => {
      setPulseHigh((current) => !current);
    }, PULSE_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [scanRunning]);

  useEffect(() => {
    if (
      !autoStart ||
      autoStartAttemptedRef.current ||
      normalizedStatus !== 'not_started' ||
      !hasConnectedEvidence ||
      typeof onStart !== 'function'
    ) {
      return;
    }

    autoStartAttemptedRef.current = true;
    setStarting(true);

    Promise.resolve(onStart())
      .catch(() => {
        autoStartAttemptedRef.current = false;
      })
      .finally(() => {
        if (mountedRef.current) setStarting(false);
      });
  }, [
    autoStart,
    hasConnectedEvidence,
    normalizedStatus,
    onStart,
  ]);

  useEffect(() => {
    if (
      normalizedStatus === 'failed' ||
      normalizedStatus === 'complete' ||
      normalizedStatus === 'running' ||
      normalizedStatus === 'in_progress'
    ) {
      autoStartAttemptedRef.current = true;
    }
  }, [normalizedStatus]);

  const action = scanComplete ? (
    <button type="button" className="mission-primary-button" onClick={onComplete} disabled={typeof onComplete !== 'function'}>Continue to Mission Brief</button>
  ) : scanRunning ? (
    <button type="button" className="mission-secondary-button" disabled>
      Mission Scan Running
    </button>
  ) : scanFailed ? (
    <button
      type="button"
      className="mission-primary-button"
      onClick={onRetry}
      disabled={
        typeof onRetry !== 'function' ||
        retrying ||
        starting
      }
    >
      {retrying ? 'Retrying Mission Scan…' : 'Retry Mission Scan'}
    </button>
  ) : (
    <span role="status" className="mission-scan-awaiting-dispatch">Preparing Mission Scan…</span>
  );

  const statusContext = scanComplete
    ? 'S3 workflow completed'
    : scanFailed
      ? 'Explicit retry available'
      : scanRunning
        ? 'Evidence workflow running'
        : 'Ready to begin';

  return (
    <div className="mission-step-shell mission-scan-page">
      <MissionCard
        eyebrow="Phase 4 · Mission Scan"
        title={
          scanComplete
            ? 'Mission Scan complete'
            : scanFailed
              ? 'Mission Scan needs attention'
              : 'OASYS is building your first operational baseline'
        }
        description={
          scanComplete
            ? authoritativeServices ? 'The scan has recorded service-level observations. Review coverage and limitations; no compliance assurance is asserted.' : 'The bounded S3 evidence-assessment workflow has completed. Unified four-service collection has not been established. This does not establish SOC 2 compliance, operating effectiveness, or independent assurance.'
            : scanFailed
              ? 'OASYS preserved the failed attempt and its failure details. Retry creates a new governed attempt without overwriting prior history.'
              : 'OASYS reports progress from the governed evidence workflow. The displayed percentage reflects persisted worker checkpoints.'
        }
        action={action}
      >
        <div className="mission-scan-console">
          <div
            className={`mission-scan-core ${coreStateClass} ${pulseStateClass}`.trim()}
            aria-hidden="true"
          >
            <span className="mission-scan-ring mission-scan-ring-outer" />
            <span className="mission-scan-ring mission-scan-ring-middle" />
            <span className="mission-scan-ring mission-scan-ring-inner" />
          </div>

          <div className="mission-scan-feed">
            <AnimatedProgressStrip value={progress} label="Mission Scan" />

            <div
              className="mission-scan-context-row"
              aria-label="Mission Scan context"
            >
              <span>{selectedConnectorCount || 0} systems selected</span>
              <span>
                {connectedConnectorCount || 0} evidence connections active
              </span>
              <span>{statusContext}</span>
              {latestAttempt?.attempt_number ? (
                <span>Attempt {latestAttempt.attempt_number}</span>
              ) : null}
            </div>

            <section aria-label="AWS service collection coverage" className="mission-scan-service-coverage">
              <h3>AWS service coverage</h3>
              {authoritativeServices ? (
                <ul>
                  {Object.entries(serviceLabels).map(([key, label]) => {
                    const detail = authoritativeServices[key];
                    return <li key={key}><strong>{label}</strong>: {String(detail.state).replaceAll('_', ' ')}
                      {detail.coverage ? ` · Coverage: ${String(detail.coverage).replaceAll('_', ' ')}` : ''}
                    </li>;
                  })}
                </ul>
              ) : (
                <p>Unified four-service results are not yet available for this scan attempt. The existing scan may cover S3 only; IAM, CloudTrail and AWS Config collection is not established by this view.</p>
              )}
              <p>Collection and coverage are not evidence of compliance or operating effectiveness.</p>
            </section>

            <div className="mission-scan-step-list">
              {steps.map((step) => (
                <div
                  className={`mission-scan-step mission-scan-step-${
                    step.status || 'pending'
                  }`}
                  key={step.label}
                >
                  <span>
                    {step.status === 'complete'
                      ? '✓'
                      : step.status === 'active'
                        ? '•'
                        : step.status === 'failed'
                          ? '!'
                          : '○'}
                  </span>
                  <p>
                    {step.label}
                    {step.status === 'complete' ? '' : '...'}
                  </p>
                </div>
              ))}
            </div>

            {scanError ? (
              <div className="connector-launch-error" role="alert">
                <strong>
                  {scanFailed
                    ? 'Mission Scan attempt failed.'
                    : 'Mission Scan status could not be refreshed.'}
                </strong>
                <span>{scanError}</span>
                {latestAttempt?.failure_message ? (
                  <small>{latestAttempt.failure_message}</small>
                ) : null}
                {typeof onRefresh === 'function' ? (
                  <button
                    type="button"
                    onClick={onRefresh}
                    disabled={retrying || starting}
                  >
                    Refresh Scan Status
                  </button>
                ) : null}
              </div>
            ) : null}

            {scanComplete ? (
              <div className="mission-scan-complete-panel" role="status">
                <div>
                  <span>Mission Scan Complete</span>
                  <p>
                    Operational baseline, readiness context, recommendation
                    inputs, and Mission Execution preparation are ready for the
                    executive brief.
                  </p>
                </div>
                <button
                  type="button"
                  className="mission-primary-button mission-scan-complete-action"
                  onClick={onComplete}
                >
                  Continue to Executive Brief
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </MissionCard>
    </div>
  );
}
